import {
  SessionInfo,
  OrganisationDetail,
  OrganisationSummary,
  OrganisationCreatePayload,
  MemberListItem,
  MembershipCreatePayload,
  InvitationPublic,
  InvitationCreated,
  InvitationPreview,
  MembershipRoleUpdatePayload,
  ProductInstancePublic,
  OrgCatalogue,
  BillingAccount,
  BillingAccountUpdate,
  PaymentMethodSummary,
  InvoiceSummary,
  InvoiceDetail,
  UpcomingInvoice,
  InvoicePayResult,
  CreditNoteSummary,
  AuditEventItem,
  AdminStats,
  AdminOrgItem,
  AdminUserItem,
  AdminProductItem,
  PlatformSettings,
  PlatformSettingsUpdate,
  UUID,
  CheckoutView,
  CheckoutCreatePayload,
  CheckoutBillingInput,
  CheckoutConfirmResult,
  CheckoutPayResult,
  CompanyLookup,
  DomainSearchResult,
  SignupView,
  SignupStartPayload,
  BillingSubscription,
  ChangeKind,
  ChangeQuote,
  ChangeResult,
  PriceInterval,
  AdminPlan,
  AdminPlanWrite,
  AdminOrgCommerce,
  AdminInvoice,
  DiscountTerms,
  EntitlementValue,
} from '../types';

export class ApiError extends Error {
  status: number;
  data: any;

  constructor(status: number, message: string, data?: any) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const headers = new Headers(options.headers || {});
  
  if (options.body && typeof options.body === 'string' && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const res = await fetch(path, {
    ...options,
    headers,
    credentials: 'same-origin',
  });

  if (!res.ok) {
    let errorDetail = `Request failed (${res.status})`;
    let errorData: any = null;
    try {
      errorData = await res.json();
      if (errorData?.detail) {
        if (typeof errorData.detail === 'string') {
          errorDetail = errorData.detail;
        } else if (typeof errorData.detail?.message === 'string') {
          errorDetail = errorData.detail.message;
        } else if (Array.isArray(errorData.detail)) {
          errorDetail = errorData.detail.map((d: any) => d.msg || JSON.stringify(d)).join(', ');
        }
      }
    } catch {
      // Body not JSON
    }
    throw new ApiError(res.status, errorDetail, errorData);
  }

  if (res.status === 204) {
    return {} as T;
  }

  return res.json();
}

export const api = {
  auth: {
    getSession: () => request<SessionInfo>('/auth/session'),
    switchOrg: (org_id: UUID) =>
      request<{ ok: boolean; active_org_id: string }>('/auth/session/org', {
        method: 'POST',
        body: JSON.stringify({ org_id }),
      }),
    logoutUrl: '/auth/logout',
    loginUrl: '/auth/login',
    silentUrl: '/auth/silent',
    switchUrl: '/auth/switch',
    localLogoutUrl: '/auth/local-logout',
    signupUrl: (returnTo: string) => `/auth/signup?return_to=${encodeURIComponent(returnTo)}`,
    loginReturnUrl: (returnTo: string, prompt?: string, loginHint?: string) =>
      `/auth/login?return_to=${encodeURIComponent(returnTo)}${prompt ? `&prompt=${prompt}` : ''}${
        loginHint ? `&login_hint=${encodeURIComponent(loginHint)}` : ''
      }`,
  },
  signup: {
    get: () => request<SignupView>('/api/v1/signup'),
    start: (payload: SignupStartPayload) =>
      request<SignupView>('/api/v1/signup', { method: 'POST', body: JSON.stringify(payload) }),
    resend: () => request<SignupView>('/api/v1/signup/resend', { method: 'POST' }),
    verify: (code: string) =>
      request<{ login_url: string }>('/api/v1/signup/verify', {
        method: 'POST',
        body: JSON.stringify({ code }),
      }),
  },
  checkout: {
    open: (payload: CheckoutCreatePayload) =>
      request<CheckoutView>('/api/v1/checkout/sessions', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
    get: (id: UUID) => request<CheckoutView>(`/api/v1/checkout/sessions/${id}`),
    confirm: (id: UUID) =>
      request<CheckoutConfirmResult>(`/api/v1/checkout/sessions/${id}/confirm`, { method: 'POST' }),
    saveBilling: (id: UUID, payload: CheckoutBillingInput) =>
      request<CheckoutView>(`/api/v1/checkout/sessions/${id}/billing`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      }),
    pay: (id: UUID, payload: { accept_terms: boolean; immediate_access: boolean; lang: 'fr' | 'en' }) =>
      request<CheckoutPayResult>(`/api/v1/checkout/sessions/${id}/pay`, {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
    refresh: (id: UUID) =>
      request<CheckoutView>(`/api/v1/checkout/sessions/${id}/refresh`, { method: 'POST' }),
    lookupCompany: (siren: string) =>
      request<CompanyLookup>(`/api/v1/checkout/company/${encodeURIComponent(siren)}`),
    searchDomains: (q: string) =>
      request<DomainSearchResult[]>(`/api/v1/checkout/domains/search?q=${encodeURIComponent(q)}`),
  },
  orgs: {
    listMine: () => request<OrganisationSummary[]>('/orgs/me'),
    create: (payload: OrganisationCreatePayload) =>
      request<OrganisationDetail>('/orgs', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
    get: (orgId: UUID) => request<OrganisationDetail>(`/orgs/${orgId}`),
    update: (orgId: UUID, payload: Record<string, any>) =>
      request<OrganisationDetail>(`/orgs/${orgId}`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      }),
    listMembers: (orgId: UUID) => request<MemberListItem[]>(`/orgs/${orgId}/members`),
    listInvitations: (orgId: UUID) => request<InvitationPublic[]>(`/orgs/${orgId}/invitations`),
    inviteMember: (orgId: UUID, payload: MembershipCreatePayload) =>
      request<InvitationCreated>(`/orgs/${orgId}/invitations`, {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
    revokeInvitation: (orgId: UUID, invitationId: UUID) =>
      request<{ ok: boolean }>(`/orgs/${orgId}/invitations/${invitationId}`, {
        method: 'DELETE',
      }),
    updateMemberRole: (orgId: UUID, membershipId: UUID, payload: MembershipRoleUpdatePayload) =>
      request<{ ok: boolean; role: string }>(`/orgs/${orgId}/members/${membershipId}`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      }),
    revokeMember: (orgId: UUID, membershipId: UUID) =>
      request<{ ok: boolean }>(`/orgs/${orgId}/members/${membershipId}`, {
        method: 'DELETE',
      }),
    listProducts: (orgId: UUID) => request<ProductInstancePublic[]>(`/orgs/${orgId}/products`),
    catalogue: (orgId: UUID, locale?: string) =>
      request<OrgCatalogue>(`/orgs/${orgId}/catalogue${locale ? `?locale=${encodeURIComponent(locale)}` : ''}`),
    listActivity: (orgId: UUID) => request<AuditEventItem[]>(`/orgs/${orgId}/activity`),
  },
  billing: {
    account: (orgId: UUID) => request<BillingAccount>(`/orgs/${orgId}/billing/account`),
    updateAccount: (orgId: UUID, payload: BillingAccountUpdate) =>
      request<BillingAccount>(`/orgs/${orgId}/billing/account`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      }),
    paymentMethods: (orgId: UUID) => request<PaymentMethodSummary[]>(`/orgs/${orgId}/billing/payment-methods`),
    invoices: (orgId: UUID) => request<InvoiceSummary[]>(`/orgs/${orgId}/billing/invoices`),
    invoice: (orgId: UUID, invoiceId: UUID) =>
      request<InvoiceDetail>(`/orgs/${orgId}/billing/invoices/${invoiceId}`),
    payInvoice: (orgId: UUID, invoiceId: UUID) =>
      request<InvoicePayResult>(`/orgs/${orgId}/billing/invoices/${invoiceId}/pay?lang=en`, { method: 'POST' }),
    refreshInvoice: (orgId: UUID, invoiceId: UUID) =>
      request<InvoiceSummary>(`/orgs/${orgId}/billing/invoices/${invoiceId}/refresh`, { method: 'POST' }),
    invoicePdfUrl: (orgId: UUID, invoiceId: UUID) => `/orgs/${orgId}/billing/invoices/${invoiceId}/pdf`,
    creditNotes: (orgId: UUID) => request<CreditNoteSummary[]>(`/orgs/${orgId}/billing/credit-notes`),
    creditNotePdfUrl: (orgId: UUID, creditNoteId: UUID) =>
      `/orgs/${orgId}/billing/credit-notes/${creditNoteId}/pdf`,
    upcoming: (orgId: UUID) => request<UpcomingInvoice[]>(`/orgs/${orgId}/billing/upcoming-invoices`),
    cancelUpcoming: (orgId: UUID, subscriptionId: UUID) =>
      request<UpcomingInvoice>(`/orgs/${orgId}/billing/upcoming-invoices/${subscriptionId}/cancel`, {
        method: 'POST',
      }),
    reinstateUpcoming: (orgId: UUID, subscriptionId: UUID) =>
      request<UpcomingInvoice>(`/orgs/${orgId}/billing/upcoming-invoices/${subscriptionId}/reinstate`, {
        method: 'POST',
      }),
    subscriptions: (orgId: UUID) => request<BillingSubscription[]>(`/orgs/${orgId}/billing/subscriptions`),
    quoteChange: (orgId: UUID, subId: UUID, payload: { kind: ChangeKind; units: number; plan_code?: string }) =>
      request<ChangeQuote>(`/orgs/${orgId}/billing/subscriptions/${subId}/quote`, {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
    startChange: (
      orgId: UUID,
      subId: UUID,
      payload: { kind: ChangeKind; units: number; plan_code?: string; expected_amount_minor?: number },
    ) =>
      request<ChangeResult>(`/orgs/${orgId}/billing/subscriptions/${subId}/changes?lang=en`, {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
    refreshChange: (orgId: UUID, changeId: UUID) =>
      request<ChangeResult>(`/orgs/${orgId}/billing/changes/${changeId}/refresh`, { method: 'POST' }),
    scheduleChange: (
      orgId: UUID,
      subId: UUID,
      payload: { units: number; remove?: boolean; plan_code?: string; interval?: PriceInterval },
    ) =>
      request<BillingSubscription>(`/orgs/${orgId}/billing/subscriptions/${subId}/schedule`, {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
    clearSchedule: (orgId: UUID, subId: UUID) =>
      request<BillingSubscription>(`/orgs/${orgId}/billing/subscriptions/${subId}/schedule`, {
        method: 'DELETE',
      }),
  },
  admin: {
    getStats: () => request<AdminStats>('/admin/stats'),
    listOrgs: (search?: string) =>
      request<AdminOrgItem[]>(`/admin/organisations${search ? `?search=${encodeURIComponent(search)}` : ''}`),
    createOrg: (payload: OrganisationCreatePayload) =>
      request<OrganisationDetail>('/admin/organisations', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
    updateOrg: (orgId: UUID, payload: Record<string, unknown>) =>
      request<OrganisationDetail>(`/admin/organisations/${orgId}`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      }),
    enterOrg: (orgId: UUID) =>
      request<{ ok: boolean; org_id: string; slug: string; membership_id: string; role: string }>(
        `/admin/organisations/${orgId}/enter`,
        { method: 'POST' }
      ),
    listUsers: (search?: string) =>
      request<AdminUserItem[]>(`/admin/users${search ? `?search=${encodeURIComponent(search)}` : ''}`),
    setPlatformAdmin: (userId: UUID, isPlatformAdmin: boolean) =>
      request<{ ok: boolean; user_id: string; is_platform_admin: boolean }>(
        `/admin/users/${userId}/platform-admin`,
        { method: 'PATCH', body: JSON.stringify({ is_platform_admin: isPlatformAdmin }) }
      ),
    listProducts: (product?: string) =>
      request<AdminProductItem[]>(`/admin/products${product ? `?product=${encodeURIComponent(product)}` : ''}`),
    listAudit: () => request<AuditEventItem[]>('/admin/audit'),
    getSettings: () => request<PlatformSettings>('/admin/settings'),
    updateSettings: (payload: PlatformSettingsUpdate) =>
      request<PlatformSettings>('/admin/settings', {
        method: 'PUT',
        body: JSON.stringify(payload),
      }),
    sendTestEmail: () =>
      request<{ ok: boolean; sent_to: string; detail: string }>('/admin/settings/email/test', {
        method: 'POST',
      }),
    listPlans: () => request<AdminPlan[]>('/admin/plans'),
    createPlan: (payload: AdminPlanWrite) =>
      request<AdminPlan>('/admin/plans', { method: 'POST', body: JSON.stringify(payload) }),
    updatePlan: (code: string, payload: AdminPlanWrite) =>
      request<AdminPlan>(`/admin/plans/${encodeURIComponent(code)}`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      }),
    commerce: (orgId: UUID) => request<AdminOrgCommerce>(`/admin/organisations/${orgId}/commerce`),
    createOffer: (
      orgId: UUID,
      payload: { plan_code: string; expires_at?: string | null; note?: string; discount?: DiscountTerms | null },
    ) =>
      request<AdminOrgCommerce>(`/admin/organisations/${orgId}/offers`, {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
    revokeOffer: (orgId: UUID, offerId: UUID) =>
      request<AdminOrgCommerce>(`/admin/organisations/${orgId}/offers/${offerId}`, { method: 'DELETE' }),
    grantComped: (orgId: UUID, payload: { plan_code: string; quantity: number; reason: string }) =>
      request<AdminOrgCommerce>(`/admin/organisations/${orgId}/comped`, {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
    updateComped: (orgId: UUID, subId: UUID, payload: { quantity: number; reason: string }) =>
      request<AdminOrgCommerce>(`/admin/organisations/${orgId}/comped/${subId}`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      }),
    createDiscount: (orgId: UUID, subId: UUID, payload: DiscountTerms & { label?: string; reason: string }) =>
      request<AdminOrgCommerce>(`/admin/organisations/${orgId}/subscriptions/${subId}/discount`, {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
    endDiscount: (orgId: UUID, subId: UUID, reason: string) =>
      request<AdminOrgCommerce>(`/admin/organisations/${orgId}/subscriptions/${subId}/discount/end`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      }),
    retryProvisioning: (orgId: UUID, product: string) =>
      request<AdminOrgCommerce>(`/admin/organisations/${orgId}/instances/${encodeURIComponent(product)}/retry`, {
        method: 'POST',
      }),
    retryDomainOp: (orgId: UUID, opId: UUID) =>
      request<AdminOrgCommerce>(`/admin/organisations/${orgId}/domain-ops/${opId}/retry`, { method: 'POST' }),
    importDomain: (orgId: UUID, domain: string, expiresAt: string) =>
      request<AdminOrgCommerce>(`/admin/organisations/${orgId}/domains/import`, {
        method: 'POST',
        body: JSON.stringify({ domain, expires_at: expiresAt }),
      }),
    invoices: (orgId: UUID) => request<AdminInvoice[]>(`/admin/organisations/${orgId}/invoices`),
    refundInvoice: (orgId: UUID, invoiceId: UUID, reason: string, amountMinor?: number | null) =>
      request<AdminInvoice>(`/admin/organisations/${orgId}/invoices/${invoiceId}/refund`, {
        method: 'POST',
        body: JSON.stringify({ reason, amount_minor: amountMinor ?? null }),
      }),
    endComped: (orgId: UUID, subId: UUID, reason: string) =>
      request<AdminOrgCommerce>(`/admin/organisations/${orgId}/comped/${subId}/end`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      }),
    createOverride: (
      orgId: UUID,
      payload: { key: string; value: EntitlementValue; reason: string; expires_at?: string | null },
    ) =>
      request<AdminOrgCommerce>(`/admin/organisations/${orgId}/overrides`, {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
    endOverride: (orgId: UUID, overrideId: UUID, reason: string) =>
      request<AdminOrgCommerce>(`/admin/organisations/${orgId}/overrides/${overrideId}/end`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      }),
    renotify: (orgId: UUID) =>
      request<AdminOrgCommerce>(`/admin/organisations/${orgId}/entitlements/notify`, { method: 'POST' }),
  },

  invitations: {
    // Unauthenticated: the recipient needs to see who is inviting them before
    // deciding to sign in.
    preview: (token: string) =>
      request<InvitationPreview>(`/invitations/${encodeURIComponent(token)}`),
    accept: (token: string) =>
      request<{ ok: boolean; org_id: string; organisation_name: string; role: string }>(
        `/invitations/${encodeURIComponent(token)}/accept`,
        { method: 'POST' }
      ),
  },
};
